import {useEffect, useState, useRef} from "react";
import {useParams, Link} from "react-router-dom";
import {getFromApi, postToApi, patchToApi, deleteFromApi, uploadToApi} from "../../api/auth";
import InlineRename from "../../components/shared/InlineRename";
import ConfirmButton from "../../components/shared/ConfirmButton";

const FILE_ICONS = {
    "image":       {icon: "bi-file-image",     color: "#10b981"},
    "video":       {icon: "bi-file-play",      color: "#6366f1"},
    "audio":       {icon: "bi-file-music",     color: "#8b5cf6"},
    "pdf":         {icon: "bi-file-pdf",       color: "#ef4444"},
    "word":        {icon: "bi-file-word",      color: "#2563eb"},
    "excel":       {icon: "bi-file-excel",     color: "#16a34a"},
    "zip":         {icon: "bi-file-zip",       color: "#d97706"},
    "text":        {icon: "bi-file-text",      color: "#6b7280"},
    "code":        {icon: "bi-file-code",      color: "#0ea5e9"},
    "default":     {icon: "bi-file-earmark",   color: "#6b7280"},
};

function fileIconFor(mime = "", name = "") {
    if (mime.startsWith("image/"))  return FILE_ICONS.image;
    if (mime.startsWith("video/"))  return FILE_ICONS.video;
    if (mime.startsWith("audio/"))  return FILE_ICONS.audio;
    if (mime === "application/pdf") return FILE_ICONS.pdf;
    if (mime.includes("word") || name.endsWith(".docx") || name.endsWith(".doc")) return FILE_ICONS.word;
    if (mime.includes("excel") || mime.includes("spreadsheet") || name.endsWith(".xlsx")) return FILE_ICONS.excel;
    if (mime.includes("zip") || mime.includes("tar") || mime.includes("rar") || name.endsWith(".zip")) return FILE_ICONS.zip;
    if (mime.startsWith("text/")) return FILE_ICONS.text;
    if (["js","ts","jsx","tsx","py","rb","go","java","php","html","css","json"].some(e => name.endsWith("."+e))) return FILE_ICONS.code;
    return FILE_ICONS.default;
}

function formatBytes(bytes) {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DropZone({folderId, onUploaded}) {
    const [dragging, setDragging]   = useState(false);
    const [progress, setProgress]   = useState(null);
    const [error, setError]         = useState("");
    const inputRef = useRef();

    const doUpload = async (files) => {
        if (!files?.length) return;
        setError("");
        for (const file of Array.from(files)) {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("folder_id", folderId);
            try {
                await uploadToApi("api/storage/upload", fd, setProgress);
            } catch {
                setError(`Failed to upload "${file.name}"`);
            }
        }
        setProgress(null);
        onUploaded();
    };

    const onDrop = (e) => {
        e.preventDefault(); setDragging(false);
        doUpload(e.dataTransfer.files);
    };

    return (
        <div>
            <div
                className={`rounded-3 border-2 border-dashed d-flex flex-column align-items-center justify-content-center py-4 px-3 mb-3 ${dragging ? "border-primary bg-light" : "border-secondary"}`}
                style={{borderStyle: "dashed", borderWidth: 2, cursor: "pointer", minHeight: 120, transition: "all .15s"}}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
            >
                <i className="bi bi-cloud-upload" style={{fontSize: "2rem", color: dragging ? "#6366f1" : "#9ca3af"}}/>
                <div className="mt-2 small text-muted text-center">
                    {progress !== null
                        ? `Uploading… ${progress}%`
                        : <><strong>Click</strong> or drag files here to upload</>}
                </div>
                {progress !== null && (
                    <div className="w-100 mt-2 px-4">
                        <div className="progress" style={{height: 4}}>
                            <div className="progress-bar" style={{width: `${progress}%`}}/>
                        </div>
                    </div>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                multiple
                style={{display: "none"}}
                onChange={e => doUpload(e.target.files)}
            />
            {error && <div className="text-danger small mb-2">{error}</div>}
        </div>
    );
}

function ImageThumb({fileId}) {
    const [src, setSrc] = useState(null);

    useEffect(() => {
        getFromApi(`api/storage/file/${fileId}/download?inline=1`)
            .then(res => { if (res.url) setSrc(res.url); })
            .catch(() => {});
    }, [fileId]);

    if (!src) return (
        <div style={{width:48, height:48, background:"#f3f4f6", borderRadius:4, flexShrink:0}}/>
    );
    return (
        <img
            src={src}
            alt=""
            style={{width:48, height:48, objectFit:"cover", borderRadius:4, flexShrink:0, cursor:"pointer"}}
            onClick={() => window.open(src, "_blank")}
        />
    );
}

function FileRow({file, onDelete}) {
    const isImage = file.mime_type?.startsWith("image/");
    const {icon, color} = fileIconFor(file.mime_type, file.original_name);
    const [busy, setBusy] = useState(false);

    const openInline = async () => {
        setBusy(true);
        try {
            const res = await getFromApi(`api/storage/file/${file._id}/download?inline=1`);
            if (res.url) window.open(res.url, "_blank");
        } finally { setBusy(false); }
    };

    const download = async () => {
        setBusy(true);
        try {
            const res = await getFromApi(`api/storage/file/${file._id}/download`);
            if (res.url) window.open(res.url, "_blank");
        } finally { setBusy(false); }
    };

    return (
        <div className="d-flex align-items-center gap-3 py-2 border-bottom">
            {isImage
                ? <ImageThumb fileId={file._id}/>
                : <i className={`bi ${icon} fs-4 flex-shrink-0`} style={{color, width:24}}/>
            }
            <div className="flex-grow-1 min-w-0">
                <button
                    className="btn btn-link p-0 text-start fw-medium text-truncate d-block small text-decoration-none"
                    style={{maxWidth:"100%", color:"inherit"}}
                    onClick={openInline}
                    disabled={busy}
                    title="Open in new tab"
                >
                    {file.original_name}
                </button>
                <div className="text-muted" style={{fontSize:"0.75rem"}}>{formatBytes(file.size)}</div>
            </div>
            <div className="d-flex gap-1 flex-shrink-0">
                <button className="btn btn-sm btn-outline-secondary" onClick={download} disabled={busy} title="Download">
                    <i className={`bi bi-${busy ? "hourglass-split" : "download"}`}/>
                </button>
                <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => onDelete(file._id)}>
                    <i className="bi bi-trash"/>
                </ConfirmButton>
            </div>
        </div>
    );
}

export default function StorageFolderView() {
    const {id} = useParams();
    const [data, setData]       = useState(null);
    const [name, setName]       = useState("");
    const [fileSearch, setFileSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await getFromApi(`api/storage/${id}`);
            setData(res.data || null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const handleAddFolder = async (e) => {
        e.preventDefault();
        await postToApi("api/storage/folder", {name, parent_id: id});
        setName(""); load();
    };

    const handleRename = async (fid, newName) => {
        await patchToApi(`api/storage/folder/${fid}`, {name: newName});
        load();
    };

    const handleDeleteFolder = async (fid) => {
        await deleteFromApi(`api/storage/folder/${fid}`);
        load();
    };

    const handleDeleteFile = async (fileId) => {
        await deleteFromApi(`api/storage/file/${fileId}`);
        load();
    };

    if (loading) return <div className="text-muted py-4 text-center">Loading...</div>;
    if (!data)   return <div className="py-4">Folder not found.</div>;

    const {folder, subfolders, files = [], ancestors = []} = data;

    return (
        <div>
            <div className="breadcrumb-nav mb-1">
                <Link to="/storage">Storage</Link>
                {ancestors.map(a => (
                    <span key={String(a._id)}> / <Link to={`/storage/${a._id}`}>{a.name}</Link></span>
                ))}
                {" "}/ {folder.name}
            </div>

            <div className="page-header d-flex align-items-center gap-2">
                <i className="bi bi-folder-fill" style={{fontSize: "1.5rem", color: "#8b5cf6"}}/>
                <InlineRename value={folder.name} onSave={name => handleRename(id, name)} className="fw-semibold fs-4"/>
            </div>

            <div className="row g-4">
                {/* Left — subfolders + create */}
                <div className="col-md-5">
                    <div className="panel mb-4">
                        <form onSubmit={handleAddFolder} className="row g-2 align-items-end">
                            <div className="col-8">
                                <label className="form-label small fw-medium">New subfolder</label>
                                <input className="form-control form-control-sm" placeholder="Folder name..." value={name} onChange={e => setName(e.target.value)} required/>
                            </div>
                            <div className="col-4">
                                <button type="submit" className="btn btn-dark btn-sm w-100"><i className="bi bi-folder-plus me-1"/>Create</button>
                            </div>
                        </form>
                    </div>

                    <div className="row g-2">
                        {subfolders.map(f => (
                            <div key={f._id} className="col-6">
                                <div className="panel d-flex flex-column align-items-center text-center gap-1" style={{padding: "0.875rem"}}>
                                    <i className="bi bi-folder-fill" style={{fontSize: "2rem", color: "#8b5cf6"}}/>
                                    <div className="w-100">
                                        <InlineRename value={f.name} onSave={name => handleRename(f._id, name)} className="fw-medium small"/>
                                        <div className="text-muted" style={{fontSize: "0.7rem"}}>
                                            {f.subCount || 0} sub · {f.fileCount || 0} files
                                        </div>
                                    </div>
                                    <div className="d-flex gap-1 w-100">
                                        <Link to={`/storage/${f._id}`} className="btn btn-sm btn-dark flex-grow-1" style={{fontSize: "0.75rem"}}>Open</Link>
                                        <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDeleteFolder(f._id)}>
                                            <i className="bi bi-trash"/>
                                        </ConfirmButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {!subfolders.length && (
                            <div className="col-12 text-muted small text-center py-2">No subfolders.</div>
                        )}
                    </div>
                </div>

                {/* Right — files */}
                <div className="col-md-7">
                    <div className="panel">
                        <h6 className="fw-semibold mb-3">
                            <i className="bi bi-files me-2 text-muted"/>Files
                            {files.length > 0 && <span className="badge text-bg-secondary ms-2">{files.length}</span>}
                        </h6>
                        <DropZone folderId={id} onUploaded={load}/>
                        {files.length > 0 && (
                            <div className="mb-2">
                                <input
                                    className="form-control form-control-sm"
                                    placeholder="Filter files…"
                                    value={fileSearch}
                                    onChange={e => setFileSearch(e.target.value)}
                                />
                            </div>
                        )}
                        {files.length > 0 ? (
                            <div>
                                {files
                                    .filter(f => !fileSearch || f.original_name.toLowerCase().includes(fileSearch.toLowerCase()))
                                    .map(f => <FileRow key={f._id} file={f} onDelete={handleDeleteFile}/>)
                                }
                                {fileSearch && !files.filter(f => f.original_name.toLowerCase().includes(fileSearch.toLowerCase())).length && (
                                    <div className="text-muted small text-center py-2">No files match.</div>
                                )}
                            </div>
                        ) : (
                            <div className="text-muted small text-center py-2">No files yet.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
